import { Fragment } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Cpu, FileText, Palette } from "lucide-react";
import { getDocFields, useOverlayVersion } from "../lib/overlay";
import { authEnabled } from "../auth/config";
import { AdminEditAbout } from "../components/AboutEditor";
import { ShareThoughts } from "../components/ShareThoughts";
import { paragraphs } from "../components/DetailBits";

// "About this app" — a case study in building real software with AI, written for a tech-executive
// reader. The Interactive Résumé is a personal build (Srinath's own Claude Max license).
// The first-person prose below is editable in-app (admin) and persisted via the content overlay
// under ABOUT_ID; these are the starting defaults (kept light on em-dashes — Srinath's note).
// Section keys here must stay in sync with FIELDS in AboutEditor.tsx.

const ABOUT_ID = "app-about";

const DEFAULTS: Record<string, string> = {
  lead:
    "Most résumés are a lossy compression of one’s true career narrative: a journey of decades " +
    "squeezed onto one page. This one inverts that. It stores the career losslessly and runs the " +
    "queries on demand, across industry, technology, skill, role, and era. The site is also the work " +
    "sample: a case study in building real software with AI, written for someone weighing what that " +
    "now makes possible.",
  build:
    "I started this about a month ago, but most of the work was done over the past 10 days. The app " +
    "is hosted on AWS and uses Entra ID for identity management. Claude Code built the original " +
    "frontend-only app using Opus 4.6 & 4.8. When Claude Design was released, I primed Design with the " +
    "original app and Design created a prototyping app from it - both use Vite/React/TypeScript. I " +
    "spent about 3-4 days on Design, refining until I reached a stable baseline. Design sent the design " +
    "files to Code, which caught up with all the changes made in Design. Code built everything from the " +
    "Entra ID App Registration to deploying AWS resources.",
  tools:
    "The first surprise was Claude Design. I wanted the career road shaped by the data, and no amount " +
    "of describing the curve got the geometry right, so Design did something I did not expect. Rather " +
    "than keep guessing at the math, it built a small “road editor” on the fly so I could drag the " +
    "control points to the exact shape. A tool making an ephemeral tool on the fly to further bridge " +
    "the human-machine gap.\n\n" +
    "For people who like to think in first principles, the Comments feature in Design will come in very " +
    "handy. I didn’t have to mentally keep track of granular changes - add comments, review and " +
    "modify/delete as needed, and send multiple comments to Claude at once - or send a comment right " +
    "away, as soon as you write it.\n\n" +
    "This is also where I felt a little disconnect between Design and Code. I think two things would " +
    "help narrow that gap a little - design lifecycle management and the ability to use Design as a " +
    "virtual layer in Code, rather than a stand-alone feature that creates throwaway apps.",
  howfar:
    "The harnesses, models, and context management have come a long way, of course. But I had a brief " +
    "opportunity to use Fable 5 for about a day and a half before it was pulled back. In my experience, " +
    "whenever you redirect AI within a session, you risk messing up the context when pivoting from one " +
    "intent or perspective to another. Any time I did this on Opus and other models, I’d find myself " +
    "having to rewind, not to when the redirect happened, but even earlier.",
  oneshot:
    "All the views and lens filters, tags, and entity relationships between Experience, Engagement, " +
    "Industries, Technologies, Skills, and Roles were captured in a one-shot manner by Opus 4.8. I " +
    "simply asked for the kind of filters I wanted and supplied only a single résumé - I haven’t had " +
    "to touch data modeling from Day 1.",
  systems:
    "Opus 4.8 has been like a competent engineer, making the “good type” of mistakes that you help " +
    "rectify over a few iterations. In the real world, this iterative problem-solving process helps " +
    "younger engineers learn and grow in a sustainable manner. But Fable 5 was like some legendary " +
    "senior engineer who knows every system in your company, and has an impeccable track record of " +
    "solving everything thrown their way.",
  impacts:
    "In real terms, stepping back from Fable 5 to Opus 4.8 added 4-5 days of effort. Opus 4.8 is good " +
    "at defect fixing, but Fable 5 seemed to be operating in some kind of “multi-dimensional context " +
    "matrix” that allowed it to seamlessly move between big picture and fine detail without noticeably " +
    "confusing context. On the other hand, every time Opus switched between the two, it seemed a bit " +
    "hit-or-miss. And even when Opus gets it right, it’s far slower than Fable 5 was.",
  conclusions:
    "While this was my first attempt at pure vibe-coding, two AI-assisted efforts preceded this one. In " +
    "the first, AI took a custom Amazon Connect Agent Desktop I’d hand-crafted and productionalized it: " +
    "security hardening, a real test suite, a CI/CD pipeline, an Amazon Q assist surface, and a packaged " +
    "desktop build, across a large multi-service codebase. In the second, Claude Code built a " +
    "cross-platform customer-chat app from one Kotlin Multiplatform codebase, shipping native to iOS, " +
    "Android, Desktop, and Web, on three of which I had never written an app before. Enhancement, then " +
    "zero-to-production at scale, then this.\n\n" +
    "The progression in AI’s software-engineering ability over those builds is astonishing. I can’t " +
    "wait for Fable 5 to come back though - even with more guardrails, I’d expect it to be far more " +
    "efficient than Opus 4.8.",
};

// Sections rendered after "The build, end to end" (which renders manually, with the Day-One→Now
// card directly beneath it).
const SECTIONS: { key: string; title: string }[] = [
  { key: "tools", title: "What changed how I work" },
  { key: "howfar", title: "How far it’s come" },
  { key: "oneshot", title: "One-shot data modeling" },
  { key: "systems", title: "Systems thinking vs defect fixing" },
  { key: "impacts", title: "Impacts" },
  { key: "conclusions", title: "Conclusions" },
];

const BUILT_WITH: { label: string; note: string }[] = [
  { label: "Claude (Opus → Fable 5)", note: "concept, first implementation, the visual system" },
  { label: "Claude Code", note: "the engineering workhorse: React/TS, refactors, infra" },
  { label: "Claude Design", note: "design as software, including the Eras road editor" },
  { label: "MCP servers", note: "tools wired into the build loop" },
  { label: "Amazon Connect", note: "the live “Talk to me” virtual agent" },
  { label: "AWS (CDK · S3 · CloudFront · WAF)", note: "deploy, edge, protection" },
];

const ARC: { when: string; what: string }[] = [
  { when: "Day one", what: "A prompt for an “inverse-résumé.” Within a day, a working Vite + React + TypeScript app with five lenses, drill-downs, a local visitor journey, and an AWS deployment." },
  { when: "Weeks 1–3", what: "Content depth, an enrichment layer, a knowledge-graph view, and a design language that kept tightening into an editorial register." },
  { when: "The design pivot", what: "A full visual system handed off from Claude Design over several sessions: the winding “Eras” career road, the lens carousels, a custom force-graph." },
  { when: "The AI layer", what: "A build-time corpus any LLM can consume, an Amazon Connect “Talk to me” agent, and the grounding for an in-app assistant." },
  { when: "Now", what: "EntraID sign-in and live, governed editing, moving each engagement through draft, review, and published from inside the app." },
];

export function AppAbout() {
  useOverlayVersion();
  const f = { ...DEFAULTS, ...getDocFields(ABOUT_ID) };

  return (
    <div className="wrap section appabout">
      <p className="eyebrow">Case study · building with AI</p>
      <h1 className="display" style={{ fontSize: 44, marginTop: 10 }}>
        Interactive Résumé
      </h1>
      <p className="section-sub" style={{ maxWidth: "66ch" }}>
        {f.lead}
      </p>

      {authEnabled && <AdminEditAbout id={ABOUT_ID} defaults={DEFAULTS} />}

      <div className="appabout-grid">
        <div className="appabout-story">
          <h2 className="display ds-h2">The build, end to end</h2>
          {paragraphs(f.build)}
          <div className="card card-pad appabout-arc-card">
            <ol className="appabout-arc">
              {ARC.map((a) => (
                <li key={a.when}>
                  <span className="appabout-arc-when mono">{a.when}</span>
                  <span className="appabout-arc-what">{a.what}</span>
                </li>
              ))}
            </ol>
          </div>

          {SECTIONS.map((s) => (
            <Fragment key={s.key}>
              <h2 className="display ds-h2">{s.title}</h2>
              {paragraphs(f[s.key])}
            </Fragment>
          ))}

          <div className="appabout-actions">
            <Link to="/design-system" className="btn btn-primary btn-sm">
              <Palette size={15} /> Design system <ArrowRight size={14} />
            </Link>
            <a href="/llms.txt" className="btn btn-sm" target="_blank" rel="noopener">
              <FileText size={15} /> AI-readable corpus
            </a>
          </div>
        </div>

        <aside className="appabout-side">
          <ShareThoughts />
          <div className="card card-pad appabout-built">
            <p className="ds-card-kicker mono">
              <Cpu size={13} /> Built with
            </p>
            <ul className="appabout-built-list">
              {BUILT_WITH.map((b) => (
                <li key={b.label}>
                  <span className="appabout-built-label">{b.label}</span>
                  <span className="appabout-built-note">{b.note}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="card card-pad appabout-meta">
            <p className="ds-card-kicker mono">Honest about it</p>
            <p className="ds-card-sub">
              This blends my own writing with AI-assisted drafts, all based on my real work and reviewed by me
              before publishing, the same draft-to-published discipline that governs every engagement here. AI
              got it most of the way; the judgment, and the sign-off, are mine.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}
